/* eslint-disable react/jsx-props-no-spreading */
import {
    useCallback, useMemo,
} from 'react'
import {
    GridFilterInputValueProps, gridFilterModelSelector, useGridSelector,
} from '@mui/x-data-grid-pro'
import {
    Autocomplete, Checkbox, Chip, ListItemText, TextField,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import merge from 'lodash/merge'

import {
    getIncludeFiltersFromFilterModel,
    getOtherFilterForBodyFromFilterModel,
} from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid'
import useStyles from './ArrayFilterAutocomplete.styles'
import useDropdownWithLoadingWhenMounting from './hooks/useDropdownWithLoadingWhenMounting'
import useLoadFilterArrayOptions from './hooks/useLoadFilterArrayOptions'

export type OptionType = {
    value: string | number,
    count: number,
    label: string,
}

export type ArrayFilterAutocompleteInputProps = GridFilterInputValueProps & {
    requestBaseUrl: string,
    requestData?: object,
    dataSourceKey: string,
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

export default function ArrayFilterAutocompleteInput({
    item,
    applyValue,
    apiRef,
    requestBaseUrl,
    requestData,
    dataSourceKey,
}: Readonly<ArrayFilterAutocompleteInputProps>) {
    const {
        classes,
        cx,
    } = useStyles()

    const filterModel = useGridSelector(apiRef, gridFilterModelSelector)

    const preDefinedFilters = useMemo(() => {
        const filtersFromModel = {
            includeFilters: getIncludeFiltersFromFilterModel(filterModel, item.field),
            ...getOtherFilterForBodyFromFilterModel(filterModel),
        }

        return merge(filtersFromModel, requestData || {})
    }, [
        filterModel,
        item,
        requestData,
    ])

    const {
        data,
    } = useLoadFilterArrayOptions<OptionType[]>({
        keyForArchived: dataSourceKey,
        baseUrl: requestBaseUrl,
        filterField: item.field,
        preDefinedFilters,
    })

    const options = useMemo(() => {
        return data || []
    }, [data])

    const sortedOptions = useMemo(() => {
        return [...options].sort((a, b) => {
            const aIsSelected = item.value?.includes(a.value)
            const bIsSelected = item.value?.includes(b.value)

            // put selected options on top
            if (aIsSelected && !bIsSelected) return -1
            if (!aIsSelected && bIsSelected) return 1

            // if both are selected or both are not selected, sort by label
            return a.label.localeCompare(b.label)
        })
    }, [
        item.value,
        options,
    ])

    const {
        isLoadingOptions,
        mountedOptions,
        onOpen,
        onClose,
        isOpen,
    } = useDropdownWithLoadingWhenMounting({
        data: sortedOptions,
    })

    const value = useMemo(() => {
        return options.filter((option) => {
            return (item.value || []).includes(option.value as string)
        }) || []
    }, [
        item.value,
        options,
    ])

    const onChange = useCallback((_, newValue: OptionType[]) => {
        const result = newValue.map((option) => {
            return option.value
        })

        applyValue({
            ...item, value: result.length > 0 ? result : undefined,
        })
    }, [
        applyValue,
        item,
    ])

    return (
        <div className={classes.wrapper}>
            <Autocomplete
                value={value}
                open={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                onChange={onChange}
                loading={isLoadingOptions}
                options={mountedOptions}
                multiple
                size="small"
                loadingText="Loading..."
                disableCloseOnSelect
                className={classes.autocomplete}
                getOptionLabel={(option) => {
                    return option.label
                }}
                renderOption={(AutoCompleteProps, option, {
                    selected,
                }) => {
                    const {
                        key, ...optionProps
                    } = AutoCompleteProps

                    return (
                        <li
                            key={key}
                            {...optionProps}
                            className={cx(classes.option, optionProps.className)}
                        >
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{
                                    marginRight: 8,
                                }}
                                checked={selected}
                            />
                            <ListItemText
                                id={key}
                                primary={option.label}
                                className={classes.optionLabel}
                            />
                            <Chip
                                size="small"
                                label={option.count}
                            />
                        </li>
                    )
                }}
                noOptionsText="No options"
                renderInput={(params) => {
                    return (
                        <TextField
                            variant="standard"
                            placeholder={value.length === 0 ? 'Select' : undefined}
                            {...params}
                        />
                    )
                }}
                componentsProps={{
                    popper: {
                        className: classes.dropdown,
                    },
                }}
            />
        </div>
    )
}
